import React, { useState, useEffect, createContext, useContext, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const FeatureAccessContext = createContext({});

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const FEATURE_LABELS = {
    sections: { en: "30 Sections", bn: "৩০ সেকশন" },
    builder: { en: "Sentence Builder", bn: "বাক্য বিল্ডার" },
    lesson: { en: "Lesson", bn: "পাঠ" },
    drills: { en: "Drills", bn: "ড্রিল" },
    spelling: { en: "Spelling", bn: "স্পেলিং" },
    mistakes: { en: "Common Mistakes", bn: "সাধারণ ভুল" },
    chatbot: { en: "Grammar Chatbot", bn: "গ্রামার চ্যাটবট" },
};

export const FEATURE_ROUTE_MAP = {
    '/learning-hub': 'sections',
    '/sentence-builder': 'builder',
    '/lesson': 'lesson',
    '/drills': 'drills',
    '/spelling-builder': 'spelling',
    '/common-mistakes': 'mistakes',
};

export function FeatureAccessProvider({ children }) {
    const { user, isAuthenticated } = useAuth();
    const [access, setAccess] = useState(null);
    const [isFreeTierUser, setIsFreeTierUser] = useState(false);
    const [freeTierConfig, setFreeTierConfig] = useState(null);
    const [loading, setLoading] = useState(true);

    const fetchAccess = useCallback(async () => {
        if (!isAuthenticated || user?.role !== 'student') {
            setAccess(null);
            setIsFreeTierUser(false);
            setFreeTierConfig(null);
            setLoading(false);
            return;
        }
        try {
            const res = await axios.get(`${API}/student/my-access`);
            setAccess(res.data.feature_access);
            setIsFreeTierUser(res.data.is_free_user || false);
            setFreeTierConfig(res.data.free_tier_config || null);
        } catch {
            setAccess(null);
        } finally {
            setLoading(false);
        }
    }, [isAuthenticated, user?.role]);

    useEffect(() => {
        fetchAccess();
    }, [fetchAccess]);

    const isLocked = (featureKey) => {
        if (!access) return false;
        // Free tier users get access to sections, spelling, mistakes, drills (with limited content inside)
        if (isFreeTierUser && freeTierConfig) {
            const freeTierFeatures = ['sections', 'spelling', 'mistakes', 'drills'];
            if (freeTierFeatures.includes(featureKey)) return false;
        }
        return access[featureKey] === false;
    };

    const isSectionUnlocked = (sectionId) => {
        if (!isFreeTierUser || !freeTierConfig) return true;
        const unlocked = freeTierConfig.sections_unlocked || [];
        return unlocked.includes(sectionId);
    };

    const isSpellingTabUnlocked = (tabKey) => {
        if (!isFreeTierUser || !freeTierConfig) return true;
        const unlocked = freeTierConfig.spelling_unlocked_tabs || [];
        return unlocked.includes(tabKey);
    };

    const isMistakeCategoryUnlocked = (categoryKey) => {
        if (!isFreeTierUser || !freeTierConfig) return true;
        const unlocked = freeTierConfig.mistakes_unlocked_categories || [];
        return unlocked.includes(categoryKey);
    };

    const getDrillsDailyLimit = () => {
        if (!isFreeTierUser || !freeTierConfig) return null;
        return freeTierConfig.drills_daily_limit ?? 3;
    };

    const isSubsectionUnlocked = (sectionId, subsectionId) => {
        if (!isFreeTierUser || !freeTierConfig) return true;
        // If section itself is locked, all subsections are locked
        const unlockedSections = freeTierConfig.sections_unlocked || [];
        if (!unlockedSections.includes(sectionId)) return false;
        // If no subsections_unlocked config for this section, all are unlocked
        const subConfig = freeTierConfig.subsections_unlocked || {};
        const allowedSubs = subConfig[String(sectionId)];
        if (!allowedSubs || allowedSubs.length === 0) return true;
        return allowedSubs.includes(subsectionId);
    };

    const isTopicUnlocked = (subsectionId, topicId) => {
        if (!isFreeTierUser || !freeTierConfig) return true;
        const topicConfig = freeTierConfig.topics_unlocked || {};
        const allowedTopics = topicConfig[String(subsectionId)];
        // No config for this subsection = all topics unlocked
        if (!allowedTopics || allowedTopics.length === 0) return true;
        return allowedTopics.includes(topicId);
    };

    return (
        <FeatureAccessContext.Provider value={{
            access, loading, isLocked, refetch: fetchAccess,
            isFreeTierUser, freeTierConfig,
            isSectionUnlocked, isSubsectionUnlocked, isTopicUnlocked, isSpellingTabUnlocked,
            isMistakeCategoryUnlocked, getDrillsDailyLimit
        }}>
            {children}
        </FeatureAccessContext.Provider>
    );
}

export const useFeatureAccess = () => useContext(FeatureAccessContext);
